@use "../../../../scss/settings/colors";
@use "../../../../scss/settings/breakpoints";

.page_title {
  color: colors.$base-black;
  font-size: 26px;
}

.page_description {
  margin-bottom: 16px;
  font-family: 'Medium Inter', sans-serif;
}

.document_header {
  display: flex;
  justify-content: space-between;
  align-items: center;

  .actions_group {
    display: flex;
    align-items: center;
    gap: 8px;
  }
}

.add_button {
  width: 50px !important;
  height: 45px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  flex-shrink: 0;
  border-radius: 20% !important;
  background-color: colors.$gray-variant !important;
}

.upload_container {
  position: relative;
  width: fit-content;

  .upload_form {
    position: absolute;
    top: 0;
    left: 100%;
    margin-left: 8px;
    z-index: 1000;
    background-color: colors.$base-white;
    width: 430px;
    height: 165px;
  }
}

.form_box {
  position: absolute;
  z-index: 5;
  animation: pulse 0.5s ease-in-out;
  border-color: colors.$brand-purple;
}

.form_upload {
  border-color: colors.$brand-purple;
  margin-left: 2rem;
}

.breadcrumbs {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  color: colors.$gray-variant-16;

  .documentViewText {
    text-decoration-color: colors.$brand-purple;
    text-decoration-thickness: 1.5px;
    text-underline-offset: 6px;
    font-size: 14px;
  }

  h3.documentViewText {
    font-size: 15px;
    line-height: 24px;
    cursor: pointer;
  }

  a, h3 {
    text-decoration: underline;
    color: colors.$brand-purple;

    &:hover {
      text-decoration: none;
    }

  }

}

.separator {
  color: colors.$gray-variant-16;
}

// ---------- Layout: aside + library block ----------
$aside-width: 220px;
$aside-radius: 16px;
$aside-active-bg: colors.$light-purple;

.library_layout {
  display: flex;
  align-items: flex-start;
  gap: 20px;
}

.aside {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: 16px;
  width: $aside-width;
  padding: 12px;
  background-color: colors.$base-white;
  border: 1px solid colors.$gray-variant-4;
  border-radius: $aside-radius;
}

.aside_libraries {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

// Library switcher items (Business Kitz Library / Own Library).
.aside_nav_item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 38px;
  padding: 8px 10px;
  font-family: 'Medium Inter', sans-serif;
  font-size: 14px;
  line-height: 20px;
  text-align: left;
  color: colors.$dark-blue;
  background-color: transparent;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: color 0.2s, background-color 0.2s;

  &:hover {
    color: colors.$brand-purple;
  }

  &:focus-visible {
    outline: 2px solid colors.$brand-purple;
    outline-offset: 1px;
  }

  &.aside_nav_item_active {
    font-family: 'SemiBold Inter', sans-serif;
    color: colors.$brand-purple;
    background-color: $aside-active-bg;
  }
}

.aside_nav_icon:global(.MuiSvgIcon-root),
svg.aside_nav_icon {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  fill: currentColor;
}

.aside_nav_item_text {
  min-width: 0;
}

.aside_divider {
  height: 1px;
  margin: 0 10px;
  background-color: colors.$gray-variant-4;
}

.aside_section {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.aside_section_header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 0 10px;
}

.aside_label:global(.MuiTypography-root) {
  margin: 0;
  font-family: 'SemiBold Inter', sans-serif;
  font-size: 12px !important;
  line-height: 18px;
  color: colors.$gray-variant-16;

  @media #{breakpoints.$tablet-mobile} {
    display: none;
  }

}

.aside_country .aside_label:global(.MuiTypography-root) {
  padding: 0 10px;
}

// Categories: vertical MUI tabs styled as nav items (horizontal on tablet / mobile).
// Scoped under `.aside` so it outranks the global `.updated-variant .MuiTabs-root` rules
// (scss/common/_tab-panel.scss), which fix the scroller / tab height to 38px for horizontal tabs
// and would otherwise clip the vertical list down to its first item.
.aside .category_tabs:global(.MuiTabs-root) {
  min-height: 0;
  margin-bottom: 0;

  :global(.MuiTabs-scroller) {
    height: auto;
  }

  :global(.MuiTabs-flexContainer) {
    gap: 2px;
  }

  :global(.MuiButtonBase-root.MuiTab-root) {
    align-items: flex-start;
    justify-content: center;
    min-width: 0;
    max-width: none;
    height: auto;
    min-height: 36px;
    margin-right: 0;
    padding: 8px 10px;
    font-family: 'Medium Inter', sans-serif;
    font-size: 14px;
    line-height: 20px;
    text-align: left;
    text-transform: none;
    color: colors.$dark-blue;
    border-radius: 8px;
    transition: color 0.2s, background-color 0.2s;

    &:hover {
      color: colors.$brand-purple;
    }

    &:focus-visible {
      outline: 2px solid colors.$brand-purple;
      outline-offset: -2px;
    }

    &:global(.Mui-selected) {
      font-family: 'SemiBold Inter', sans-serif;
      color: colors.$brand-purple;
      background-color: $aside-active-bg;
    }
  }

  // Scroll arrows of the horizontal (tablet / mobile) strip.
  :global(.MuiButtonBase-root.MuiTabScrollButton-root) {
    width: 28px;
    height: auto;
    min-height: 36px;
    margin-right: 0;
    border-radius: 8px;
  }

  // The active item is marked by its background, as in the mockup.
  :global(.MuiTabs-indicator) {
    display: none;
  }
}

.aside_bottom {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

// Overrides the global `.region-select-box` width (scss/common/_form.scss).
.aside :global(.region-select-box).aside_select {
  width: 100%;
}

// "How the Document Library works": opens the library overview tooltip (same as the Document
// Vault aside).
.aside_help {
  display: inline-flex;
  align-items: flex-start;
  align-self: flex-start;
  gap: 6px;
  padding: 4px 10px;
  font-family: 'Medium Inter', sans-serif;
  font-size: 12px !important;
  text-align: left;
  line-height: 18px;
  color: colors.$brand-purple;
  text-decoration: underline;
  text-underline-offset: 2px;
  background: none;
  border: none;
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    text-decoration: none;
  }

  &:focus-visible {
    outline: 2px solid colors.$brand-purple;
    outline-offset: 1px;
  }

  @media #{breakpoints.$mobile}{
    display: none;
  }

}

.aside_help_icon:global(.MuiSvgIcon-root) {
  font-size: 15px;
}

.aside_filter {
  :global(.MuiButton-root) {
    justify-content: flex-start;
    width: 100%;
  }
}

// ---------- Library block: action bar + table ----------
$library-block-radius: 16px;
$toolbar-control-height: 40px;
$switcher-bg: #eef0f4;

.library_block {
  flex: 1;
  min-width: 0;

  // Card view: the action bar is a rounded block of its own above the cards.
  .toolbar {
    margin-bottom: 15px;
    border: 1px solid colors.$gray-variant-4;
    border-radius: $library-block-radius;
  }
}

// List view: the action bar and the table form one rounded block, split by a divider.
.library_block_joined {
  overflow: hidden;
  background-color: colors.$base-white;
  border: 1px solid colors.$gray-variant-4;
  border-radius: $library-block-radius;

  .toolbar {
    margin-bottom: 0;
    border: none;
    border-bottom: 1px solid colors.$gray-variant-4;
    border-radius: 0;
  }
}

.library_block_content {
  position: relative;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  background-color: colors.$base-white;
}

// The extra classes outrank the global `.updated-variant .MuiInputBase-root` / `.search-input`
// height rules (scss/common/_form.scss).
.toolbar .toolbar_search:global(.MuiTextField-root) {
  flex: 1;
  min-width: 0;

  :global(.MuiInputBase-root.MuiOutlinedInput-root) {
    height: $toolbar-control-height !important;
    padding-left: 12px;
    border-radius: 10px !important;
    font-family: 'Regular Inter', sans-serif;
    font-size: 14px;
    color: colors.$dark-blue;
    background-color: colors.$base-white;

    :global(.MuiOutlinedInput-notchedOutline) {
      border-color: colors.$gray-variant-4;
    }
  }

    &:hover:not(:global(.Mui-disabled)) :global(.MuiOutlinedInput-notchedOutline) {
      border-color: colors.$border-grey;
    }

    &:global(.Mui-focused) :global(.MuiOutlinedInput-notchedOutline) {
      border-width: 1px;
      border-color: colors.$brand-purple;
    }
  }

  :global(.MuiInputBase-input) {
    height: $toolbar-control-height !important;
    padding: 0;
    box-sizing: border-box;

    &::placeholder {
      color: colors.$gray-variant-16;
      opacity: 1;
    }


  :global(.MuiInputAdornment-positionStart) {
    margin-right: 8px;
  }
}

.toolbar_search_icon:global(.MuiSvgIcon-root) {
  width: 18px;
  height: 18px;
  color: colors.$gray-variant-16;
}

// Card / list switcher: icon-only segmented control.
.card_list_view_container {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 2px;
  height: $toolbar-control-height;
  padding: 3px;
  background-color: $switcher-bg;
  border-radius: 10px;
}

.variant_btn {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 34px;
  height: 34px;
  padding: 0;
  color: colors.$dark-blue;
  background-color: transparent;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: color 0.2s, background-color 0.2s, box-shadow 0.2s;

  &[disabled] {
    opacity: 0.5;
  }

  &:hover:not([disabled]) {
    color: colors.$brand-purple;
  }

  &.variant_btn_active:not([disabled]) {
    color: colors.$brand-purple;
    background-color: colors.$base-white;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.12);
  }

  &:focus-visible {
    outline: 2px solid colors.$brand-purple;
    outline-offset: 1px;
  }

  svg {
    width: 16px;
    height: 16px;
    fill: currentColor !important;
  }
}

// Document Terms button.
.toolbar_icon_btn:global(.MuiIconButton-root) {
  flex-shrink: 0;
  width: $toolbar-control-height;
  height: $toolbar-control-height;
  color: colors.$dark-blue;
  background-color: colors.$base-white;
  border: 1px solid colors.$gray-variant-4;
  border-radius: 10px;

  &:hover {
    color: colors.$brand-purple;
    border-color: colors.$border-grey;
    background-color: colors.$base-white;
  }
}

.visually_hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}

// ---------- Responsive ----------
.form_box {

  @media #{breakpoints.$mobile}{
    position: fixed;
    top: 50%;
    left: 16px;
    right: 16px;
    margin-left: 0;
    max-height: calc(100vh - 32px);
    overflow-y: auto;
    transform: translateY(-50%);
    animation: none;
    z-index: 1300;
  }

}

.form_upload {

  @media #{breakpoints.$mobile}{
    margin-left: 0;
  }

}

.upload_container .upload_form {

  @media #{breakpoints.$mobile}{
    position: fixed;
    top: 50%;
    left: 16px;
    right: 16px;
    width: auto;
    height: auto;
    margin-left: 0;
    transform: translateY(-50%);
    z-index: 1300;
  }

}

.document_header {
  flex-wrap: wrap;
  gap: 8px;
}


.toolbar {

  @media #{breakpoints.$small-mobile}{
    padding: 12px;
  }

}

// Tablet / mobile: the aside goes above the action bar + table / cards block.
.library_layout {

  @media #{breakpoints.$tablet-mobile}{
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

}

.aside {

  @media #{breakpoints.$tablet-mobile}{
    width: 100%;
    gap: 12px;
  }

}

.aside_libraries {

  @media #{breakpoints.$tablet-mobile}{
    flex-direction: row;
    flex-wrap: wrap;
  }

}

.aside_nav_item {

  @media #{breakpoints.$tablet-mobile}{
    width: auto;
  }

}

.aside_divider {

  @media #{breakpoints.$tablet-mobile}{
    margin: 0;
  }

}

.aside .category_tabs:global(.MuiTabs-root) :global(.MuiButtonBase-root.MuiTab-root) {

  @media #{breakpoints.$tablet-mobile}{
    flex-shrink: 0;
    align-items: center;
    white-space: nowrap;
  }

}

// Country selector and requirement filter side by side, wrapping on narrow screens.
.aside_bottom {

  @media #{breakpoints.$tablet-mobile}{
    flex-direction: row;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 12px;
  }

}

.aside_country {

  @media #{breakpoints.$tablet-mobile}{
    flex: 0 1 260px;
    min-width: 200px;
  }

  @media #{breakpoints.$small-mobile}{
    flex: 1 1 100%;
  }

}

.aside_filter {

  @media #{breakpoints.$tablet-mobile}{
    flex: 0 1 auto;
  }

  @media #{breakpoints.$small-mobile}{
    flex: 1 1 100%;
  }

}
